import { useQuery } from '@tanstack/react-query';
import { type DashboardExportResponse, dashboardExportResponseSchema } from '@tempo-io/roadmap-dto';

interface UseExportDashboardParams {
  dashboardId: number;
  token: string;
}

export type UseExportDashboardResult =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'success'; data: DashboardExportResponse };

export function useExportDashboard({
  dashboardId,
  token,
}: UseExportDashboardParams): UseExportDashboardResult {
  const query = useQuery({
    queryKey: ['export-dashboard', dashboardId, token],
    queryFn: async () => {
      const url = `/export-api/v1/loop-visualization/dashboard/${dashboardId}?token=${encodeURIComponent(token)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error('Invalid or expired export token');
        }
        if (response.status === 404) {
          throw new Error('Dashboard not found');
        }
        throw new Error(`Failed to fetch dashboard: ${response.statusText}`);
      }

      const parsed = dashboardExportResponseSchema.safeParse(await response.json());
      if (!parsed.success) {
        throw new Error(`Malformed dashboard export payload: ${parsed.error.message}`);
      }

      return parsed.data;
    },
    retry: false, // The token is short-lived; a retry would just fail again.
    staleTime: 0,
  });

  if (query.isError) {
    return {
      status: 'error',
      message: query.error instanceof Error ? query.error.message : 'Unknown error',
    };
  }

  if (query.data) {
    return { status: 'success', data: query.data };
  }

  return { status: 'loading' };
}
